import { useState, useEffect } from "react";
import { useAdmin, UserSubscription } from "@/hooks/useAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  MoreHorizontal, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Clock,
  Eye,
  Check,
  X
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface PaymentRecord {
  id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  amount: number;
  currency: string;
  payment_method: string;
  status: 'pending' | 'verified' | 'failed' | 'cancelled';
  transaction_id?: string;
  created_at: string;
  verified_at?: string;
  verified_by?: string;
  notes?: string;
}

export function PaymentStatus() {
  const { user: adminUser } = useAuth();
  const { getUserSubscriptions } = useAdmin();
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [subscriptions, setSubscriptions] = useState<UserSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | PaymentRecord['status']>("all");
  const [filterMethod, setFilterMethod] = useState<string>("all");
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);

  useEffect(() => {
    fetchPayments();
    fetchSubscriptions();
  }, []);

  const fetchPayments = async () => {
    try {
      const { data, error } = await fetch('/api/admin/payments', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminUser?.session?.access_token}`
        }
      });

      if (error) throw error;
      setPayments(data || []);
    } catch (error: any) {
      console.error('Error fetching payments:', error);
      toast.error('Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    const subs = await getUserSubscriptions();
    setSubscriptions(subs);
  };

  const getSubscriptionName = (userId: string) => {
    const subscription = subscriptions.find(sub => sub.user_id === userId);
    return subscription?.subscription_type || 'Basic';
  };

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = payment.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         payment.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || payment.status === filterStatus;
    const matchesMethod = filterMethod === "all" || payment.payment_method === filterMethod;
    
    return matchesSearch && matchesStatus && matchesMethod;
  });

  const handleVerifyPayment = async (paymentId: string) => {
    try {
      const { error } = await fetch(`/api/admin/payments/${paymentId}/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminUser?.session?.access_token}`
        }
      });

      if (error) throw error;
      
      setPayments(payments.map(p => 
        p.id === paymentId 
          ? { ...p, status: 'verified', verified_at: new Date().toISOString(), verified_by: adminUser?.id }
          : p
      ));
      toast.success('Pagamento verificado com sucesso');
    } catch (error: any) {
      console.error('Error verifying payment:', error);
      toast.error('Erro ao verificar pagamento');
    }
  };

  const handleRejectPayment = async (paymentId: string) => {
    if (!confirm('Tem certeza que deseja rejeitar este pagamento?')) {
      return;
    }

    try {
      const { error } = await fetch(`/api/admin/payments/${paymentId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminUser?.session?.access_token}`
        }
      });

      if (error) throw error;
      
      setPayments(payments.map(p => 
        p.id === paymentId 
          ? { ...p, status: 'failed', verified_at: new Date().toISOString(), verified_by: adminUser?.id }
          : p
      ));
      toast.success('Pagamento rejeitado');
    } catch (error: any) {
      console.error('Error rejecting payment:', error);
      toast.error('Erro ao rejeitar pagamento');
    }
  };

  const handleBulkVerify = async () => {
    if (selectedPayments.length === 0) {
      toast.error('Selecione pelo menos um pagamento');
      return;
    }

    try {
      const { error } = await fetch('/api/admin/payments/bulk-verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminUser?.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ paymentIds: selectedPayments })
      });

      if (error) throw error;
      
      setPayments(payments.map(p => 
        selectedPayments.includes(p.id)
          ? { ...p, status: 'verified', verified_at: new Date().toISOString(), verified_by: adminUser?.id }
          : p
      ));
      setSelectedPayments([]);
      toast.success(`${selectedPayments.length} pagamentos verificados`);
    } catch (error: any) {
      console.error('Error bulk verifying payments:', error);
      toast.error('Erro ao verificar pagamentos em lote');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusIcon = (status: PaymentRecord['status']) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'cancelled':
        return <X className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status: PaymentRecord['status']) => {
    const variants = {
      pending: "secondary",
      verified: "default",
      failed: "destructive",
      cancelled: "outline"
    } as const;

    const labels = {
      pending: "Pendente",
      verified: "Verificado",
      failed: "Falhou",
      cancelled: "Cancelado"
    } as const;

    return (
      <Badge variant={variants[status]}>
        {labels[status]}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Verificação de Pagamentos</span>
            <div className="flex gap-2">
              {selectedPayments.length > 0 && (
                <Button 
                  onClick={handleBulkVerify}
                  variant="default"
                  size="sm"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Verificar Selecionados ({selectedPayments.length})
                </Button>
              )}
            </div>
          </CardTitle>
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar pagamentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-3 py-2 border rounded-md bg-background"
              >
                <option value="all">Todos os Status</option>
                <option value="pending">Pendente</option>
                <option value="verified">Verificado</option>
                <option value="failed">Falhou</option>
                <option value="cancelled">Cancelado</option>
              </select>
              <select
                value={filterMethod}
                onChange={(e) => setFilterMethod(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background"
              >
                <option value="all">Todos os Métodos</option>
                <option value="credit_card">Cartão de Crédito</option>
                <option value="bank_transfer">Transferência Bancária</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input
                    type="checkbox"
                    checked={selectedPayments.length === filteredPayments.length && filteredPayments.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPayments(filteredPayments.map(p => p.id));
                      } else {
                        setSelectedPayments([]);
                      }
                    }}
                  />
                </TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Método</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Nenhum pagamento encontrado com os filtros atuais.
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedPayments.includes(payment.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedPayments([...selectedPayments, payment.id]);
                          } else {
                            setSelectedPayments(selectedPayments.filter(id => id !== payment.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{payment.user_name || 'Usuário sem nome'}</div>
                        <div className="text-sm text-muted-foreground">{payment.user_email || 'Sem email'}</div>
                      </div>
                    </TableCell>
                    <TableCell>{formatCurrency(payment.amount, payment.currency)}</TableCell>
                    <TableCell>{payment.payment_method}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(payment.status)}
                        {getStatusBadge(payment.status)}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(payment.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Abrir menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/admin/payments/${payment.id}`}>
                              <Eye className="mr-2 h-4 w-4" />
                              Ver Detalhes
                            </Link>
                          </DropdownMenuItem>
                          {payment.status === 'pending' && (
                            <DropdownMenuItem 
                              onClick={() => handleVerifyPayment(payment.id)}
                              className="text-green-600 focus:text-green-600"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              Verificar
                            </DropdownMenuItem>
                          )}
                          {payment.status === 'pending' && (
                            <DropdownMenuItem 
                              onClick={() => handleRejectPayment(payment.id)}
                              className="text-red-600 focus:text-red-600"
                            >
                              <XCircle className="mr-2 h-4 w-4" />
                              Rejeitar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
